export type VimMode = "normal" | "command";

export interface VimState {
  mode: VimMode;
  cursor: number;
  charIndex: number;
  desiredCharIndex: number;
  lineCount: number;
  commandInput: string;
  pendingKey: string | null;
  pageSize: number;
}

export function initialState(lineCount: number, pageSize: number = 20): VimState {
  return { mode: "normal", cursor: 0, charIndex: 0, desiredCharIndex: 0, lineCount, commandInput: "", pendingKey: null, pageSize };
}

function nextWordStart(text: string, from: number): number | null {
  let i = from;
  while (i < text.length && !/\s/.test(text[i])) i++;
  while (i < text.length && /\s/.test(text[i])) i++;
  return i < text.length ? i : null;
}

function prevWordStart(text: string, from: number): number | null {
  if (from <= 0) return null;
  let i = from - 1;
  while (i > 0 && /\s/.test(text[i])) i--;
  if (/\s/.test(text[i])) return null;
  while (i > 0 && !/\s/.test(text[i - 1])) i--;
  return i;
}

function clampToLine(desired: number, lineLen: number): number {
  return lineLen > 0 ? Math.min(desired, lineLen - 1) : 0;
}

export function handleKey(
  state: VimState,
  key: string,
  ctrlKey: boolean,
  lines?: string[]
): VimState {
  const last = state.lineCount - 1;

  if (state.mode === "command") {
    if (key === "Escape") return { ...state, mode: "normal", commandInput: "" };
    if (key === "Enter") return { ...state, mode: "normal", commandInput: "" };
    if (key === "Backspace") return { ...state, commandInput: state.commandInput.slice(0, -1) };
    if (key.length === 1) return { ...state, commandInput: state.commandInput + key };
    return state;
  }

  if (key === "Escape") return state;

  if (ctrlKey && key === "d") {
    const next = Math.min(state.cursor + Math.floor(state.pageSize / 2), last);
    return { ...state, cursor: next, charIndex: 0, desiredCharIndex: 0 };
  }
  if (ctrlKey && key === "u") {
    const next = Math.max(state.cursor - Math.floor(state.pageSize / 2), 0);
    return { ...state, cursor: next, charIndex: 0, desiredCharIndex: 0 };
  }

  if (key === "j") {
    const newCursor = Math.min(state.cursor + 1, last);
    const newLineLen = lines ? (lines[newCursor]?.length ?? 0) : 0;
    const newCharIndex = lines ? clampToLine(state.desiredCharIndex, newLineLen) : state.charIndex;
    return { ...state, cursor: newCursor, charIndex: newCharIndex, pendingKey: null };
  }
  if (key === "k") {
    const newCursor = Math.max(state.cursor - 1, 0);
    const newLineLen = lines ? (lines[newCursor]?.length ?? 0) : 0;
    const newCharIndex = lines ? clampToLine(state.desiredCharIndex, newLineLen) : state.charIndex;
    return { ...state, cursor: newCursor, charIndex: newCharIndex, pendingKey: null };
  }
  if (key === "G") return { ...state, cursor: last, charIndex: 0, desiredCharIndex: 0, pendingKey: null };

  if (key === "g") {
    if (state.pendingKey === "g") return { ...state, cursor: 0, charIndex: 0, desiredCharIndex: 0, pendingKey: null };
    return { ...state, pendingKey: "g" };
  }

  if (key === "h") {
    if (state.charIndex > 0) {
      const c = state.charIndex - 1;
      return { ...state, charIndex: c, desiredCharIndex: c, pendingKey: null };
    }
    if (!lines || state.cursor === 0) return state;
    const prevLen = lines[state.cursor - 1]?.length ?? 0;
    const c = Math.max(prevLen - 1, 0);
    return { ...state, cursor: state.cursor - 1, charIndex: c, desiredCharIndex: c, pendingKey: null };
  }

  if (key === "l") {
    if (!lines) return state;
    const lineLen = lines[state.cursor]?.length ?? 0;
    if (state.charIndex < lineLen - 1) {
      const c = state.charIndex + 1;
      return { ...state, charIndex: c, desiredCharIndex: c, pendingKey: null };
    }
    if (state.cursor < last) {
      return { ...state, cursor: state.cursor + 1, charIndex: 0, desiredCharIndex: 0, pendingKey: null };
    }
    return state;
  }

  if (key === "b") {
    if (!lines) return state;
    const pos = prevWordStart(lines[state.cursor] ?? "", state.charIndex);
    if (pos !== null) return { ...state, charIndex: pos, desiredCharIndex: pos, pendingKey: null };
    if (state.cursor > 0) {
      const prevText = lines[state.cursor - 1] ?? "";
      const c = prevWordStart(prevText, prevText.length) ?? 0;
      return { ...state, cursor: state.cursor - 1, charIndex: c, desiredCharIndex: c, pendingKey: null };
    }
    return state;
  }

  if (key === "$") {
    if (!lines) return state;
    const lineLen = lines[state.cursor]?.length ?? 0;
    const c = Math.max(lineLen - 1, 0);
    return { ...state, charIndex: c, desiredCharIndex: c, pendingKey: null };
  }

  if (key === "w") {
    if (!lines) return state;
    const pos = nextWordStart(lines[state.cursor] ?? "", state.charIndex);
    if (pos !== null) return { ...state, charIndex: pos, desiredCharIndex: pos, pendingKey: null };
    if (state.cursor < last) return { ...state, cursor: state.cursor + 1, charIndex: 0, desiredCharIndex: 0, pendingKey: null };
    return state;
  }

  if (key === ":") return { ...state, mode: "command", pendingKey: null };

  return { ...state, pendingKey: null };
}
